import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AdminLayout from '../../components/AdminLayout';

const LARGE_PROPERTY_THRESHOLD_SQM = 5000;
const LARGE_PROPERTY_SURCHARGE = 110;
const inputCls = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500';

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function AdminBookForClient() {
  const { clientId, assignmentId } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('admin_token');

  const [client, setClient] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [price, setPrice] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertySizeSqm, setPropertySizeSqm] = useState('');
  const [notes, setNotes] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingTime, setBookingTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [travelLoading, setTravelLoading] = useState(false);
  const [travelQuote, setTravelQuote] = useState(null);
  const [travelFee, setTravelFee] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const travelDebounceRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    (async () => {
      const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { navigate('/admin/login'); return; }
      const data = await res.json();
      const a = (data.assignments || []).find((x) => x.id === assignmentId);
      if (!a || !a.custom_packages) { alert('Package not found for this client.'); navigate('/admin/clients'); return; }
      const p = a.custom_packages;
      setClient(data);
      setInvoiceEmail(data.email || '');
      setPkg(p);
      setPrice(String(a.price_override != null ? a.price_override : (p.default_price || 0)));
    })().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, assignmentId]);

  useEffect(() => {
    setTravelQuote(null);
    if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current);
    const trimmed = propertyAddress.trim();
    if (trimmed.length < 6) { setTravelLoading(false); return; }
    setTravelLoading(true);
    travelDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${backendUrl}/api/bookings/travel-quote?address=${encodeURIComponent(trimmed)}`);
        const q = await res.json();
        setTravelQuote(q);
        if (q.success) setTravelFee(String(Number(q.travel_fee || 0)));
      } catch { setTravelQuote({ success: false, message: 'Could not estimate travel time' }); }
      finally { setTravelLoading(false); }
    }, 800);
    return () => { if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current); };
  }, [propertyAddress, backendUrl]);

  const sizeNum = parseInt(propertySizeSqm, 10);
  const largeSurcharge = !isNaN(sizeNum) && sizeNum > LARGE_PROPERTY_THRESHOLD_SQM ? LARGE_PROPERTY_SURCHARGE : 0;
  const addonsTotal = selectedAddOns.reduce((s, a) => s + Number(a.price || 0), 0);
  const finalTotal = Number(price || 0) + addonsTotal + largeSurcharge + Number(travelFee || 0);

  const toggleAddon = (a) => setSelectedAddOns((prev) => prev.some((x) => x.name === a.name) ? prev.filter((x) => x.name !== a.name) : [...prev, a]);

  const handleDateChange = async (date) => {
    setBookingDate(date); setBookingTime(''); setAvailableSlots([]);
    if (!date) return;
    setLoadingSlots(true);
    try {
      const res = await fetch(`${backendUrl}/api/bookings/available-slots?date=${toDateStr(date)}&package_name=${encodeURIComponent(pkg.name)}&duration=${pkg.duration_minutes || 120}`);
      const data = await res.json();
      setAvailableSlots(data.available_slots || []);
      if ((data.available_slots || []).length) setBookingTime(data.available_slots[0]);
    } catch { setAvailableSlots([]); }
    finally { setLoadingSlots(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!bookingDate || !bookingTime) { alert('Select a date and an open time.'); return; }
    setSubmitting(true);
    try {
      const payload = {
        assignment_id: assignmentId,
        property_address: propertyAddress,
        property_size_sqm: !isNaN(sizeNum) && sizeNum ? sizeNum : null,
        booking_date: toDateStr(bookingDate),
        booking_time: bookingTime,
        add_ons: selectedAddOns,
        notes,
        policy_accepted: true,
        invoice_email: invoiceEmail || null,
        package_price: Number(price || 0),
        travel_fee: Number(travelFee || 0),
      };
      const res = await fetch(`${backendUrl}/api/admin/clients/${clientId}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      alert(`Booked for ${client.name}. A confirmation email has been sent to them.`);
      navigate('/admin/bookings');
    } catch (err) { alert(`Could not book: ${err.message}`); }
    finally { setSubmitting(false); }
  };

  if (!pkg) return <AdminLayout><div className="text-center py-12 text-gray-500">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto" data-testid="admin-book-for-client-page">
        <Link to="/admin/clients" className="text-sm text-teal-600 hover:text-teal-700" data-testid="back-to-clients-link"><i className="ri-arrow-left-line"></i> Back to clients</Link>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 mt-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-teal-700">Booking on behalf of</p>
          <h2 className="text-2xl font-bold text-gray-900 mt-1" data-testid="book-for-client-name">{client.name}</h2>
          <p className="text-sm text-gray-600">{client.email}</p>
          <div className="mt-4 flex justify-between items-center">
            <span className="font-semibold text-gray-900" data-testid="book-for-client-package">{pkg.name}</span>
            <span className="text-sm text-gray-600">{pkg.duration_minutes || 120} min on-site</span>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price for this booking (A$) *</label>
              <input required type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} data-testid="admin-book-price-input" />
              <p className="text-xs text-gray-500 mt-1">Change it for split filming, e.g. half on each day.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Send invoice to</label>
              <input type="email" value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} className={inputCls} data-testid="admin-book-invoice-email-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Address *</label>
            <input required type="text" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="e.g. 123 Kings Rd, Pimlico QLD 4812" className={inputCls} data-testid="admin-book-address-input" />
            {travelLoading && <p className="text-xs text-gray-500 mt-1">Estimating travel time…</p>}
            {travelQuote && travelQuote.message && (
              <p className={`text-xs mt-1 ${travelQuote.success ? 'text-amber-600' : 'text-red-600'}`} data-testid="admin-book-travel-message">
                {travelQuote.message}{!travelQuote.success && ' — enter the travel fee manually below.'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Travel fee (A$)</label>
              <input type="number" step="0.01" min="0" value={travelFee} onChange={(e) => setTravelFee(e.target.value)} className={inputCls} data-testid="admin-book-travel-fee-input" />
              <p className="text-xs text-gray-500 mt-1">Auto-filled, set to 0 to remove.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Size (sqm)</label>
              <input type="number" min="0" value={propertySizeSqm} onChange={(e) => setPropertySizeSqm(e.target.value)} className={inputCls} data-testid="admin-book-size-input" />
              {largeSurcharge > 0 && <p className="text-xs text-amber-600 mt-1">Large property surcharge +A${largeSurcharge}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes for the crew</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Split shoot – day 2 of 2" className={inputCls} data-testid="admin-book-notes-input" />
            </div>
          </div>

          {(pkg.add_ons || []).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Add-ons</label>
              <div className="grid grid-cols-2 gap-2">
                {pkg.add_ons.map((a, i) => {
                  const active = selectedAddOns.some((x) => x.name === a.name);
                  return (
                    <button type="button" key={i} onClick={() => toggleAddon(a)} data-testid={`admin-book-addon-${i}`} className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${active ? 'bg-teal-50 border-teal-500 text-teal-800' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex justify-between"><span>{a.name}</span><span className="font-medium">+A${Number(a.price).toFixed(2)}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <DatePicker selected={bookingDate} onChange={handleDateChange} minDate={new Date()} filterDate={(d) => d.getDay() !== 0} placeholderText="Pick a date" className={inputCls} dateFormat="yyyy-MM-dd" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
              {loadingSlots ? (
                <div className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500">Loading slots…</div>
              ) : availableSlots.length === 0 ? (
                <div className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500" data-testid="admin-book-no-slots">{bookingDate ? 'No slots available.' : 'Pick a date first.'}</div>
              ) : (
                <select value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className={`${inputCls} bg-white`} data-testid="admin-book-time-select">
                  {availableSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-gray-700"><span>Package</span><span>A${Number(price || 0).toFixed(2)}</span></div>
            {selectedAddOns.map((a, i) => (
              <div key={i} className="flex justify-between text-gray-700"><span>{a.name}</span><span>+A${Number(a.price).toFixed(2)}</span></div>
            ))}
            {largeSurcharge > 0 && <div className="flex justify-between text-gray-700"><span>Large property surcharge</span><span>+A${largeSurcharge}</span></div>}
            {Number(travelFee) > 0 && <div className="flex justify-between text-gray-700"><span>Travel fee</span><span>+A${Number(travelFee).toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-lg text-gray-900 pt-2"><span>Total</span><span data-testid="admin-book-total">A${finalTotal.toFixed(2)} incl GST</span></div>
          </div>

          <button type="submit" disabled={submitting} data-testid="admin-book-submit" className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white py-3 rounded-lg font-semibold">
            {submitting ? 'Booking…' : `Book for ${client.name}`}
          </button>
          <p className="text-xs text-gray-500 text-center">They'll get the normal confirmation email and see it in their portal.</p>
        </form>
      </div>
    </AdminLayout>
  );
}
